import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FiSearch,
  FiFilter,
  FiDownload,
  FiUser,
  FiDollarSign,
  FiCalendar,
  FiPrinter,
  FiRefreshCw,
  FiCreditCard,
  FiX
} from "react-icons/fi";
import "./Payments.css";
const url = process.env.REACT_APP_BACKEND_URL;

const Payments = () => {
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState("");
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState({ from: "", to: "" });
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("all");
  const [stats, setStats] = useState({
    totalPaid: 0,
    remainingBalance: 0,
    paymentCount: 0,
    totalPaidBalance: 0,
    planAmount: 0,
    accountName: ""
  });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [bankSummary, setBankSummary] = useState([]);
  const [showBankSummary, setShowBankSummary] = useState(false);
  const [bankTypes, setBankTypes] = useState([]);

  useEffect(() => {
    fetchMembers();
    fetchBankSummary();
    fetchBankTypes();
  }, []);

  useEffect(() => {
    if (selectedMember) {
      fetchMemberPayments(selectedMember);
    }
  }, [selectedMember]);

  useEffect(() => {
    filterPayments();
  }, [payments, searchTerm, dateFilter, paymentTypeFilter]);

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${url}/api/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data.data || response.data);
    } catch (error) {
      console.error("Error fetching members:", error);
      toast.error("Failed to load members");
    }
  };

  const fetchBankTypes = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${url}/api/bank/list/simple`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBankTypes(response.data);
    } catch (error) {
      console.error("Error fetching bank types:", error);
    }
  };

  const fetchBankSummary = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${url}/api/payments/bank-summary`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (response.data.success) {
        setBankSummary(response.data.banks);
      }
    } catch (error) {
      console.error("Error fetching bank summary:", error);
    }
  };

  const fetchMemberPayments = async (memberId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${url}/api/payments/history/${memberId}`,
        { from: dateFilter.from, to: dateFilter.to },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Use the transformed data from backend
      setPayments(response.data.payments || []);

      if (response.data.member) {
        // FIX: Use the last payment's remainingBalance for the stats
        const paymentsData = response.data.payments || [];
        const lastPayment = paymentsData[paymentsData.length - 1];
        const calculatedRemainingBalance = lastPayment?.remainingBalance || 0;

        const statsData = {
          totalPaid: paymentsData.reduce(
            (sum, payment) => sum + (payment.amount || 0),
            0
          ),
          remainingBalance: calculatedRemainingBalance, // Use calculated balance instead of member.remainingBalance
          paymentCount: paymentsData.length,
          totalPaidBalance: response.data.member.totalPaidBalance || 0,
          planAmount: response.data.member.planAmount || 0,
          accountName: response.data.member.planName || "N/A"
        };

        setStats(statsData);
      }
    } catch (error) {
      console.error("Error fetching payments:", error);
      if (error.response?.status === 404) {
        setPayments([]);
        setStats({
          totalPaid: 0,
          remainingBalance: 0,
          paymentCount: 0,
          totalPaidBalance: 0,
          planAmount: 0,
          accountName: ""
        });
        toast.info("No payments found for this member");
      } else {
        toast.error("Failed to load payment history");
      }
    } finally {
      setLoading(false);
    }
  };

  const filterPayments = () => {
    let filtered = payments;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (payment) =>
          (payment.remarks?.toLowerCase() || "").includes(term) ||
          (payment.bank?.toLowerCase() || "").includes(term) ||
          (payment.type?.toLowerCase() || "").includes(term) ||
          (payment.paymentId?.toLowerCase() || "").includes(term) ||
          (payment.mobile?.toLowerCase() || "").includes(term)
      );
    }

    if (paymentTypeFilter !== "all") {
      filtered = filtered.filter(
        (payment) => payment.type === paymentTypeFilter
      );
    }

    if (dateFilter.from || dateFilter.to) {
      filtered = filtered.filter((payment) => {
        const paymentDate = new Date(payment.paidAt);

        if (dateFilter.from) {
          const fromDate = new Date(dateFilter.from);
          if (paymentDate < fromDate) return false;
        }

        if (dateFilter.to) {
          const toDate = new Date(dateFilter.to);
          toDate.setHours(23, 59, 59, 999);
          if (paymentDate > toDate) return false;
        }

        return true;
      });
    }

    setFilteredPayments(filtered);
  };

  const handleDateFilterChange = (e) => {
    const { name, value } = e.target;
    setDateFilter(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyDateFilter = () => {
    if (selectedMember) {
      fetchMemberPayments(selectedMember);
    } else {
      filterPayments();
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setDateFilter({ from: "", to: "" });
    setPaymentTypeFilter("all");
    if (selectedMember) {
      fetchMemberPayments(selectedMember);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPaymentTypeBadge = (type) => {
    const typeStyles = {
      registration: "badge-primary",
      monthly: "badge-secondary",
      other: "badge-tertiary",
    };

    return (
      <span className={`badge ${typeStyles[type] || "badge-tertiary"}`}>
        {type || "Unknown"}
      </span>
    );
  };

  const getBankName = (bankId) => {
    if (!bankId) return "N/A";
    const bank = bankTypes.find(b => b._id === bankId);
    return bank ? `${bank.name} (${bank.number})` : "N/A";
  };

  const getMemberName = (memberId) => {
    if (!memberId) return "N/A";
    const member = members.find(m => m._id === memberId);
    return member ? `${member.fname} ${member.mname} ${member.lname}`.trim() : "N/A";
  };

  const exportToCSV = () => {
    if (filteredPayments.length === 0) return;

    const member = members.find(m => m._id === selectedMember);
    const memberName = member ? `${member.fname} ${member.mname}${member.lname}`.trim() : "Unknown";
    const memberNumber = member ? member.user_number : "N/A";

    let csvContent = "data:text/csv;charset=utf-8,\uFEFF";

    // Add header
    csvContent += "No,Member Name,Member Number,Payment Type,Account Name,Amount,Remarks,Bank,Mobile,Date,Remaining Balance\r\n";

    // Add rows with proper data mapping
    filteredPayments.forEach((payment, index) => {
      const row = [
        index + 1,
        `"${memberName}"`,
        `"${memberNumber}"`,
        payment.type,
        `"${stats.accountName}"`,
        payment.amount,
        `"${payment.remarks || ''}"`,
        `"${payment.bank || getBankName(payment.banktypeId)}"`,
        `"${payment.mobile || ''}"`,
        `"${formatDate(payment.paidAt)}"`,
        payment.remainingBalance || payment.runningBalance || 0
      ].join(',');

      csvContent += row + '\r\n';
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${memberName}_payments_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("CSV exported successfully");
  };

  const handleViewReceipt = (payment) => {
    const member = members.find(m => m._id === selectedMember);
    setSelectedPayment({
      ...payment,
      memberName: member ? `${member.fname} ${member.lname}`.trim() : "Unknown",
      memberNumber: member ? member.user_number : "N/A",
      accountName: stats.accountName,
      bankName: payment.bank || getBankName(payment.banktypeId)
    });
    setShowReceipt(true);
  };

  const closeReceipt = () => {
    setShowReceipt(false);
    setSelectedPayment(null);
  };
  const ReceiptModal = ({ payment, onClose }) => {
    if (!payment) return null;

    return (
      <div className="receipt-modal-overlay">
        <div className="receipt-modal">
          <div className="receipt-header">
            <h3>Payment Receipt</h3>
            <button className="close-btn" onClick={onClose}>
              <FiX />
            </button>
          </div>

          <div className="receipt-content" id="receipt-content">
            {/* Company Header */}
            <div className="receipt-company">
              <h3>Member Management</h3>
              <p>Payment Confirmation</p>
            </div>

            {/* Main Payment Details */}
            <div className="receipt-main-details">
              <div className="receipt-row">
                <span className="label">Member:</span>
                <span className="value">{payment.memberName || "N/A"}</span>
              </div>
              <div className="receipt-row">
                <span className="label">Member ID:</span>
                <span className="value">{payment.memberNumber || "N/A"}</span>
              </div>
              <div className="receipt-row">
                <span className="label">Payment Type:</span>
                <span className="value">{payment.type || "N/A"}</span>
              </div>
              <div className="receipt-row">
                <span className="label">Amount:</span>
                <span className="value amount">{formatCurrency(payment.amount)}</span>
              </div>
              <div className="receipt-row">
                <span className="label">Date:</span>
                <span className="value">{formatDate(payment.paidAt)}</span>
              </div>
              <div className="receipt-row">
                <span className="label">Remarks:</span>
                <span className="value">{payment.remarks || "N/A"}</span>
              </div>
              <div className="receipt-row">
                <span className="label">Bank:</span>
                <span className="value">{payment.bankName || "N/A"}</span>
              </div>
              <div className="receipt-row">
                <span className="label">Mobile:</span>
                <span className="value">{payment.mobile || "N/A"}</span>
              </div>
              <div className="receipt-row">
                <span className="label">Remaining Balance:</span>
                <span className="value">{formatCurrency(payment.remainingBalance || payment.runningBalance || 0)}</span>
              </div>
            </div>

            {/* Footer */}
            <div className="receipt-footer">
              <div className="thank-you">Thank you for your payment!</div>
              <div className="receipt-id">Receipt ID: {payment._id?.slice(-8) || "N/A"}</div>
            </div>
          </div>

          <div className="receipt-actions">
            <button className="btn-print" onClick={() => window.print()}>
              <FiPrinter /> Print Receipt
            </button>
            <button className="btn-close" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };
  return (
    <div className="payments-container">
      {showReceipt && <ReceiptModal payment={selectedPayment} onClose={closeReceipt} />}

      <div className="payments-header">
        <h2><FiDollarSign /> Payment Management System</h2>
        <p>View and manage member payments and bank transactions</p>
      </div>

      <div className="view-toggle">
        <button
          className={!showBankSummary ? "active" : ""}
          onClick={() => setShowBankSummary(false)}
        >
          <FiUser /> Member Payments
        </button>
        <button
          className={showBankSummary ? "active" : ""}
          onClick={() => setShowBankSummary(true)}
        >
          <FiCreditCard /> Bank Summary
        </button>
      </div>

      {!showBankSummary ? (
        <>
          <div className="filters-section">
            <div className="filter-group">
              <label htmlFor="memberSelect">Select Member</label>
              <select
                id="memberSelect"
                value={selectedMember}
                onChange={(e) => setSelectedMember(e.target.value)}
                className="form-select"
              >
                <option value="">Choose a member...</option>
                {members.map(member => (
                  <option key={member._id} value={member._id}>
                    {member.user_number} - {member.fname} {member.mname} {member.lname}
                  </option>
                ))}
              </select>
            </div>

            {selectedMember && (
              <>
                <div className="filter-group">
                  <label htmlFor="search">Search Payments</label>
                  <div className="search-input">
                    <FiSearch />
                    <input
                      id="search"
                      type="text"
                      placeholder="Search by remarks, bank, or mobile..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                <div className="filter-group">
                  <label htmlFor="paymentType">Payment Type</label>
                  <select
                    id="paymentType"
                    value={paymentTypeFilter}
                    onChange={(e) => setPaymentTypeFilter(e.target.value)}
                    className="form-select"
                  >
                    <option value="all">All Types</option>
                    <option value="registration">Registration</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>Date Range</label>
                  <div className="date-filters">
                    <input
                      type="date"
                      name="from"
                      value={dateFilter.from}
                      onChange={handleDateFilterChange}
                      placeholder="From"
                    />
                    <span>to</span>
                    <input
                      type="date"
                      name="to"
                      value={dateFilter.to}
                      onChange={handleDateFilterChange}
                      placeholder="To"
                    />
                    <button
                      onClick={handleApplyDateFilter}
                      className="btn-secondary"
                    >
                      <FiFilter /> Apply
                    </button>
                  </div>
                </div>

                <button onClick={handleClearFilters} className="btn-outline">
                  <FiRefreshCw /> Clear Filters
                </button>

                <button onClick={exportToCSV} className="btn-secondary" disabled={filteredPayments.length === 0}>
                  <FiDownload /> Export CSV
                </button>
              </>
            )}
          </div>

          {selectedMember && (
            <div className="stats-cards">
              <div className="stat-card">
                <div className="stat-icon">
                  <FiDollarSign />
                </div>
                <div className="stat-content">
                  <h3>{formatCurrency(stats.totalPaid)}</h3>
                  <p>Total Paid</p>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">
                  <FiUser />
                </div>
                <div className="stat-content">
                  <h3>{stats.paymentCount}</h3>
                  <p>Payment Count</p>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">
                  <FiCalendar />
                </div>
                <div className="stat-content">
                  <h3>{formatCurrency(stats.remainingBalance)}</h3>
                  <p>Remaining Balance</p>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">
                  <FiCreditCard />
                </div>
                <div className="stat-content">
                  <h3>{stats.accountName}</h3>
                  <p>Account Type</p>
                </div>
              </div>
            </div>
          )}

          {selectedMember && (
            <div className="payments-table-container">
              {loading ? (
                <div className="loading">Loading payments...</div>
              ) : filteredPayments.length > 0 ? (
                <>
                  <div className="table-header">
                    <h3>Payment History - {stats.accountName}</h3>
                    <span className="results-count">{filteredPayments.length} payments found</span>
                  </div>
                  <table className="payments-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Payment Type</th>
                        <th>Amount</th>
                        <th>Remarks</th>
                        <th>Bank</th>
                        <th>Mobile</th>
                        <th>Date</th>
                        <th>Remaining Balance</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPayments.map((payment, index) => {
                        return (
                          <tr key={payment.paymentId || payment._id || index}>
                            <td>{index + 1}</td>
                            <td>{getPaymentTypeBadge(payment.type)}</td>
                            <td className="amount">{formatCurrency(payment.amount)}</td>
                            <td className="remarks">{payment.remarks || "N/A"}</td>
                            <td>{payment.bank || getBankName(payment.banktypeId)}</td>
                            <td>{payment.mobile || "N/A"}</td>
                            <td>{formatDate(payment.paidAt)}</td>
                            <td className="balance">
                              {formatCurrency(payment.remainingBalance || payment.runningBalance || 0)}
                              {/* Debug display */}
                              <small style={{ display: 'block', color: '#666', fontSize: '10px' }}>
                                {/* debug: {payment.remainingBalance} */}
                              </small>
                            </td>
                            <td>
                              <button
                                className="btn-receipt"
                                onClick={() => handleViewReceipt(payment)}
                                title="View Receipt"
                              >
                                <FiPrinter />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              ) : (
                <div className="no-data">
                  {payments.length === 0
                    ? "No payments found for this member"
                    : "No payments match your filters"}
                </div>
              )}
            </div>
          )}

          {!selectedMember && (
            <div className="select-member-prompt">
              <div className="prompt-content">
                <FiUser size={48} />
                <h3>Select a Member</h3>
                <p>Choose a member from the dropdown to view their payment history</p>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bank-summary-section">
          <h3><FiCreditCard /> Bank Transaction Summary</h3>

          {bankSummary.length > 0 ? (
            <div className="bank-cards">
              {bankSummary.map(bank => (
                <div key={bank.bankId} className="bank-card">
                  <div className="bank-header">
                    <h4>{bank.bankName}</h4>
                    <span className="bank-number">{bank.bankNumber}</span>
                  </div>
                  <div className="bank-details">
                    <div className="bank-stat">
                      <span className="label">Current Balance:</span>
                      <span className="value">{formatCurrency(bank.currentBalance)}</span>
                    </div>
                    <div className="bank-stat">
                      <span className="label">Total Received:</span>
                      <span className="value">{formatCurrency(bank.totalReceived)}</span>
                    </div>
                    <div className="bank-stat">
                      <span className="label">Payment Count:</span>
                      <span className="value">{bank.paymentCount}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-data">No bank data available</div>
          )}
        </div>
      )}
    </div>
  );
};

export default Payments;